import { Link } from 'react-router-dom';
import ScrollReveal from '@/components/base/ScrollReveal';

const comparisonData = [
  {
    label: 'Durability',
    mass: 'Tarnishes in 2-4 weeks',
    joviaa: 'Years of daily wear',
    silver: 'Tarnishes, needs polishing',
    fine: 'Lifetime (but soft)',
  },
  {
    label: 'Water Resistance',
    mass: 'Not resistant — discolours',
    joviaa: 'Sweat-proof & water-resistant',
    silver: 'Tarnishes faster with water',
    fine: 'Resistant but avoid chlorine',
  },
  {
    label: 'Skin Safety',
    mass: 'Often contains nickel — causes allergies',
    joviaa: 'Nickel-free & hypoallergenic',
    silver: 'Generally safe (may contain nickel in alloys)',
    fine: 'Safe (14K+)',
  },
  {
    label: 'Price Range',
    mass: '\u20B9199 – \u20B9599',
    joviaa: '\u20B9499 – \u20B91,899',
    silver: '\u20B91,500 – \u20B96,000',
    fine: '\u20B98,000 – \u20B950,000+',
  },
  {
    label: 'Design Freshness',
    mass: 'Generic, copied designs',
    joviaa: 'New drops every 4-6 weeks, trend-forward',
    silver: 'Limited design cycles',
    fine: 'Classic, slow trend adoption',
  },
  {
    label: 'Daily Wearability',
    mass: 'Remove before washing hands',
    joviaa: 'Wear 24/7 — gym, shower, sleep',
    silver: 'Remove for water, exercise',
    fine: 'Too precious for daily casual wear',
  },
];

const testingCards = [
  {
    icon: 'ri-ruler-line',
    title: 'Plating Thickness Check',
    description: 'Every batch is measured for consistent PVD coating thickness (minimum 0.5 microns) using X-ray fluorescence — ensuring your gold finish lasts, not flakes.',
  },
  {
    icon: 'ri-drop-line',
    title: 'Sweat Simulation Test',
    description: 'We expose samples to a synthetic sweat solution at body temperature for 72 continuous hours. No discolouration, no corrosion — just uninterrupted gold.',
  },
  {
    icon: 'ri-water-flash-line',
    title: 'Water Immersion Test',
    description: 'Jewellery is fully submerged in water for 48 hours straight. We check for any plating lift, colour change, or base metal reaction. Spoiler: nothing happens.',
  },
  {
    icon: 'ri-test-tube-line',
    title: 'Nickel Release Test',
    description: 'Every design is lab-tested to confirm nickel release is below the EU REACH regulation threshold of 0.5 \u03BCg/cm\u00B2/week — well within the hypoallergenic safe zone.',
  },
  {
    icon: 'ri-link-unlink',
    title: 'Clasp & Link Strength Test',
    description: 'Lobster clasps, box clasps, and chain links are pull-tested to 5 kg force — far beyond everyday wear stress. Your necklace stays on, no matter what.',
  },
];

export default function DurabilityPage() {
  return (
    <main className="min-h-screen bg-background-50">
      {/* Hero Section */}
      <section className="relative bg-primary-500 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Abstract%20flowing%20liquid%20gold%20texture%20with%20warm%20amber%20and%20sand%20tones%2C%20metallic%20waves%20with%20soft%20gradient%2C%20minimal%20clean%20background%2C%20elegant%20scientific%20aesthetic%2C%20gentle%20shimmer%20and%20light%20reflection%2C%20no%20people&width=1600&height=650&seq=durability-hero-2026&orientation=landscape"
            alt=""
            className="w-full h-full object-cover object-center opacity-25"
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 md:px-6 py-20 md:py-28 flex flex-col items-center text-center">
          <p className="text-accent-400 text-xs md:text-sm font-label font-semibold uppercase tracking-[0.2em] mb-4">
            Built to Keep Up
          </p>
          <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-bold text-background-50 max-w-3xl leading-tight">
            Life-Proof Gold,<br />Explained.
          </h1>
          <p className="mt-5 md:mt-6 text-base md:text-lg text-background-50/80 font-label max-w-2xl leading-relaxed">
            We don&apos;t just say our jewellery lasts. We test it — against sweat, water, daily wear, and time. Here&apos;s exactly what makes Joviaa different.
          </p>
        </div>
      </section>

      {/* Comparison Table Section */}
      <section className="max-w-7xl mx-auto px-4 md:px-6 py-16 md:py-24">
        <ScrollReveal>
          <div className="text-center mb-10 md:mb-14">
            <p className="text-foreground-400 text-xs md:text-sm font-label font-semibold uppercase tracking-[0.2em] mb-3">
              The Difference
            </p>
            <h2 className="text-2xl md:text-4xl font-heading font-bold text-primary-500">
              How Joviaa Compares
            </h2>
            <p className="mt-3 text-foreground-600 font-label text-sm md:text-base max-w-2xl mx-auto">
              We sit in the sweet spot: more durable and safer than mass-market jewellery, more accessible and wearable than silver or fine gold.
            </p>
          </div>
        </ScrollReveal>

        {/* Desktop Table */}
        <ScrollReveal delay={100}>
          <div className="hidden md:block overflow-hidden rounded-xl border border-background-200">
            {/* Header Row */}
            <div className="grid grid-cols-[180px_1fr_1fr_1fr_1fr] bg-primary-500">
              <div className="px-5 py-4"></div>
              <div className="px-5 py-4 text-center">
                <span className="text-xs font-label font-semibold text-background-50/60 uppercase tracking-wider">
                  Mass Imitation
                </span>
              </div>
              <div className="px-5 py-4 text-center bg-accent-500 rounded-t-lg">
                <span className="text-xs font-label font-bold text-primary-500 uppercase tracking-wider">
                  Joviaa
                </span>
              </div>
              <div className="px-5 py-4 text-center">
                <span className="text-xs font-label font-semibold text-background-50/60 uppercase tracking-wider">
                  Silver / Vermeil
                </span>
              </div>
              <div className="px-5 py-4 text-center">
                <span className="text-xs font-label font-semibold text-background-50/60 uppercase tracking-wider">
                  Fine Jewellery
                </span>
              </div>
            </div>

            {/* Data Rows */}
            {comparisonData.map((row, idx) => (
              <div
                key={row.label}
                className={`grid grid-cols-[180px_1fr_1fr_1fr_1fr] ${
                  idx % 2 === 0 ? 'bg-background-50' : 'bg-primary-500/[0.02]'
                }`}
              >
                <div className="px-5 py-4 flex items-center">
                  <span className="text-sm font-heading font-semibold text-primary-500">
                    {row.label}
                  </span>
                </div>
                <div className="px-5 py-4 flex items-center justify-center">
                  <span className="text-xs md:text-sm text-foreground-500 font-label text-center leading-relaxed">
                    {row.mass}
                  </span>
                </div>
                <div className="px-5 py-4 flex items-center justify-center bg-accent-100">
                  <span className="text-xs md:text-sm text-accent-700 font-label font-semibold text-center leading-relaxed">
                    {row.joviaa}
                  </span>
                </div>
                <div className="px-5 py-4 flex items-center justify-center">
                  <span className="text-xs md:text-sm text-foreground-500 font-label text-center leading-relaxed">
                    {row.silver}
                  </span>
                </div>
                <div className="px-5 py-4 flex items-center justify-center">
                  <span className="text-xs md:text-sm text-foreground-500 font-label text-center leading-relaxed">
                    {row.fine}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </ScrollReveal>

        {/* Mobile Cards */}
        <div className="md:hidden flex flex-col gap-4">
          {comparisonData.map((row, idx) => (
            <ScrollReveal key={row.label} delay={idx * 60}>
              <div
                className="bg-background-50 rounded-xl border border-background-200 p-5"
              >
                <h3 className="text-sm font-heading font-bold text-primary-500 mb-3">
                  {row.label}
                </h3>
                <div className="flex flex-col gap-2.5">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-foreground-400 font-label">Mass Imitation</span>
                    <span className="text-xs text-foreground-500 font-label text-right max-w-[60%]">
                      {row.mass}
                    </span>
                  </div>
                  <div className="flex items-center justify-between bg-accent-100 -mx-3 px-3 py-2 rounded-md">
                    <span className="text-xs text-accent-600 font-label font-semibold">Joviaa</span>
                    <span className="text-xs text-accent-700 font-label font-semibold text-right max-w-[60%]">
                      {row.joviaa}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-foreground-400 font-label">Silver / Vermeil</span>
                    <span className="text-xs text-foreground-500 font-label text-right max-w-[60%]">
                      {row.silver}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-foreground-400 font-label">Fine Jewellery</span>
                    <span className="text-xs text-foreground-500 font-label text-right max-w-[60%]">
                      {row.fine}
                    </span>
                  </div>
                </div>
              </div>
            </ScrollReveal>
          ))}
        </div>

        <p className="mt-5 text-xs text-foreground-400 font-label text-center italic">
          Comparison based on typical product characteristics in the Indian market. Individual products may vary.
        </p>
      </section>

      {/* How We Test */}
      <section className="bg-accent-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-16 md:py-24">
          <ScrollReveal>
            <div className="text-center mb-10 md:mb-14">
              <p className="text-accent-600 text-xs md:text-sm font-label font-semibold uppercase tracking-[0.2em] mb-3">
                Behind the Promise
              </p>
              <h2 className="text-2xl md:text-4xl font-heading font-bold text-primary-500">
                How We Test Every Piece
              </h2>
              <p className="mt-3 text-foreground-600 font-label text-sm md:text-base max-w-2xl mx-auto">
                Before any Joviaa design reaches you, it goes through five quality checks — so you never have to wonder if your jewellery can handle real life.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {testingCards.map((card, idx) => (
              <ScrollReveal key={card.title} delay={idx * 100}>
                <div className="bg-background-50 rounded-xl p-5 md:p-6 flex flex-col items-center text-center group hover:bg-background-50 transition-colors">
                  <div className="w-14 h-14 rounded-full bg-accent-200 flex items-center justify-center mb-4">
                    <i className={`${card.icon} text-xl text-accent-600`}></i>
                  </div>
                  <h3 className="text-base font-heading font-semibold text-primary-500 mb-2">
                    {card.title}
                  </h3>
                  <p className="text-sm text-foreground-600 font-label leading-relaxed">
                    {card.description}
                  </p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Care Instructions */}
      <section className="max-w-7xl mx-auto px-4 md:px-6 py-16 md:py-24">
        <ScrollReveal>
          <div className="text-center mb-10 md:mb-14">
            <p className="text-foreground-400 text-xs md:text-sm font-label font-semibold uppercase tracking-[0.2em] mb-3">
              Make It Last
            </p>
            <h2 className="text-2xl md:text-4xl font-heading font-bold text-primary-500">
              How to Care for Your Joviaa
            </h2>
            <p className="mt-3 text-foreground-600 font-label text-sm md:text-base max-w-2xl mx-auto">
              Life-Proof doesn&apos;t mean zero-care. A little love goes a long way in keeping your pieces looking fresh.
            </p>
          </div>
        </ScrollReveal>

        <div className="max-w-4xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-0 rounded-xl overflow-hidden border border-background-200">
            {/* Do's */}
            <ScrollReveal direction="left" delay={0}>
              <div className="bg-background-50 p-6 md:p-8">
                <div className="flex items-center gap-2.5 mb-5">
                  <div className="w-8 h-8 rounded-full bg-accent-500 flex items-center justify-center flex-shrink-0">
                    <i className="ri-check-line text-sm text-background-50"></i>
                  </div>
                  <h3 className="text-lg font-heading font-semibold text-primary-500">
                    Do This
                  </h3>
                </div>
                <ul className="flex flex-col gap-3">
                  {[
                    'Wipe gently with a soft, dry cloth after each wear — especially after sweating or exercising.',
                    'Store each piece separately in the anti-tarnish pouch provided with your order.',
                    'Put your jewellery on last — after perfume, lotion, and makeup have dried on your skin.',
                    'Use the Joviaa gift box for longer-term storage — it\u2019s designed to protect against humidity.',
                  ].map((item) => (
                    <li key={item.slice(0, 20)} className="flex items-start gap-2.5">
                      <div className="w-5 h-5 rounded-full bg-accent-200 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-check-line text-xs text-accent-600"></i>
                      </div>
                      <span className="text-sm text-foreground-600 font-label leading-relaxed">
                        {item}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </ScrollReveal>

            {/* Don'ts */}
            <ScrollReveal direction="right" delay={150}>
              <div className="bg-secondary-100 p-6 md:p-8">
                <div className="flex items-center gap-2.5 mb-5">
                  <div className="w-8 h-8 rounded-full bg-primary-500 flex items-center justify-center flex-shrink-0">
                    <i className="ri-close-line text-sm text-background-50"></i>
                  </div>
                  <h3 className="text-lg font-heading font-semibold text-primary-500">
                    Avoid This
                  </h3>
                </div>
                <ul className="flex flex-col gap-3">
                  {[
                    'Never wear Joviaa in chlorinated water — pools and hot tubs can affect the plating over time.',
                    'Avoid direct contact with perfumes, hairsprays, and strong skincare acids before the product dries.',
                    'Don\u2019t use jewellery cleaning solutions or ultrasonic cleaners — they\u2019re too harsh for PVD plating.',
                    'Don\u2019t toss multiple pieces loose in a pouch or drawer — they can scratch each other.',
                  ].map((item) => (
                    <li key={item.slice(0, 20)} className="flex items-start gap-2.5">
                      <div className="w-5 h-5 rounded-full bg-primary-500/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-close-line text-xs text-primary-600"></i>
                      </div>
                      <span className="text-sm text-foreground-600 font-label leading-relaxed">
                        {item}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <ScrollReveal>
        <section className="bg-primary-500">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-14 md:py-20 flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-background-50">
                Still have questions about our materials?
              </h2>
              <p className="mt-2 text-sm md:text-base text-background-50/70 font-label">
                Check our FAQ or reach out on WhatsApp — we love talking about this stuff.
              </p>
            </div>
            <div className="flex items-center gap-4 flex-shrink-0 whitespace-nowrap flex-wrap">
              <Link
                to="/faq"
                className="px-7 py-3 bg-background-50 text-primary-500 rounded-lg font-heading font-semibold text-sm hover:bg-background-50/90 transition-colors cursor-pointer whitespace-nowrap"
              >
                Read FAQ
              </Link>
              <a
                href="https://wa.me/919686543847?text=Hi%20Joviaa!%20I%20have%20a%20question%20about%20your%20materials."
                target="_blank"
                rel="noopener noreferrer"
                className="px-7 py-3 border-2 border-background-50/30 text-background-50 rounded-lg font-heading font-semibold text-sm hover:border-background-50/60 transition-colors cursor-pointer whitespace-nowrap inline-flex items-center gap-2"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-whatsapp-fill"></i>
                </div>
                Ask on WhatsApp
              </a>
            </div>
          </div>
        </section>
      </ScrollReveal>
    </main>
  );
}